/**
 * THE COSMETIC VOCABULARY — key shapes and forbidden key names, and NOTHING
 * THAT KNOWS WHAT ANYONE OWNS.
 *
 * WHY THIS FILE EXISTS, stated plainly because it looks at first like a
 * gratuitous split. SIM-F2: the pay-to-win firewall was a hand-written list of
 * six modules inside the test that enforced it, and `save.ts` — the module that
 * writes the save — was not on it. A live paid xp multiplier planted on the
 * autosave path passed all 570 tests.
 *
 * The fix is not a longer list. The protected set is now DERIVED: every module
 * in this lane is protected unless it proves it cannot move a quantity, so a
 * file added tomorrow is covered without anyone remembering. See
 * `firewall.test.ts`.
 *
 * That derivation only works if the wall is in the right place, and it was not.
 * `save.ts` genuinely needs two things that lived on the wrong side of it: the
 * shape of a cosmetic key, so a wardrobe can be validated, and the list of
 * rate-shaped key names, so a forward bag can be filtered. Neither has anything
 * to do with ownership. They are moved here, and what stays in
 * `entitlements.ts` is exactly the surface that can answer "what does this
 * player have":
 *
 *   THE VOCABULARY (here)          THE OWNERSHIP (entitlements.ts)
 *   what a cosmetic key looks like  the catalogue
 *   what a rate-shaped name is      id -> grants, and the resolver over it
 *
 * A protected module may therefore carry entitlement ids as OPAQUE STRINGS. It
 * cannot resolve one into anything, because the only code that can is behind
 * the wall. That is the property the firewall now enforces, and it is a stronger
 * statement than the one it replaces.
 *
 * THIS MODULE IMPORTS NOTHING, and entitlements.ts imports only this, so there
 * is no cycle and the direction of the dependency is the direction of the rule.
 */

/** A cosmetic key. Access to an appearance, nothing else. */
export type CosmeticKey = `cosmetic.${string}`;

/**
 * Key names that would let an entitlement touch time or rate. The list is
 * deliberately wider than what a careless commit would actually add, because a
 * near-miss name is exactly how this rule dies: `boostPct` is as fatal as
 * `duration` and reads far more innocent in a diff.
 */
export const RATE_SHAPED_KEYS = [
  'duration',
  'durationMs',
  'durationHours',
  'ms',
  'seconds',
  'minutes',
  'hours',
  'days',
  'expires',
  'expiresAt',
  'expiry',
  'until',
  'ttl',
  'period',
  'window',
  'holdHours',
  'holdTicks',
  'offlineHours',
  'offlineCap',
  'rate',
  'rateMultiplier',
  'multiplier',
  'mult',
  'boost',
  'boostPct',
  'bonus',
  'speed',
  'xp',
  'xpRate',
  // `skill` is the one field in SimState that scales the earn rate, so it is
  // the one an entitlement would have to reach to sell speed. It cannot.
  'skill',
  'level',
  'proficiency',
  'yield',
  'per',
  'perSecond',
  'perTick',
  'ticks',
  'amount',
  'value',
  'coins',
] as const;

export type RateShapedKey = (typeof RATE_SHAPED_KEYS)[number];

/** true only while T has no rate-shaped key at all. */
export type AccessOnly<T> = Extract<keyof T, RateShapedKey> extends never ? true : false;

/**
 * The shape of a cosmetic key, and the only question this module can answer
 * about one. Note what it cannot tell you: whether anybody owns it.
 */
export function isCosmeticKey(value: string): value is CosmeticKey {
  return value.startsWith('cosmetic.') && value.length > 'cosmetic.'.length;
}
